'use client'

import { type ExternalToast as ToastOptions, toast } from 'sonner'

import {
  CheckCircleIcon,
  CloseIcon,
  NoticeIcon,
  WarningIcon,
  XCircleIcon,
} from '@/components/icons'

export type NotifyOptions = ToastOptions & { persistent?: boolean }

type NotifyFunction = (message: string, options?: NotifyOptions) => string | number

const createNotifier =
  (type: 'success' | 'info' | 'warning' | 'error', baseOptions?: ToastOptions): NotifyFunction =>
  (message: string, options?: NotifyOptions) => {
    const id: string | number = toast[type](message, {
      ...baseOptions,
      ...options,
      duration: options?.persistent ? Infinity : options?.duration || 5000,
      style: {
        right: '0px',
        padding: '10px 20px',
        display: 'flex',
        width: 'fit-content',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        fontSize: 22,
        fontWeight: 'bold',
        lineHeight: 'normal',
        borderRadius: '10px',
        ...baseOptions?.style,
        ...options?.style,
      },
      position: 'bottom-right',
      action: options?.persistent
        ? {
            label: <CloseIcon className="size-5" color="white" />,
            onClick: () => toast.dismiss(id),
            actionButtonStyle: {
              backgroundColor: 'currentcolor',
            },
          }
        : undefined,
      actionButtonStyle: {
        backgroundColor: 'transparent',
        padding: 0,
      },
    })

    return id
  }

const notify: {
  success: NotifyFunction
  info: NotifyFunction
  warning: NotifyFunction
  error: NotifyFunction
} = {
  success: createNotifier('success', {
    style: {
      backgroundColor: '#06C',
      color: '#FFF',
    },
    icon: <CheckCircleIcon />,
  }),
  info: createNotifier('info', {
    style: {
      backgroundColor: '#0EA5E9',
      color: '#FFF',
    },
    icon: <NoticeIcon />,
  }),
  warning: createNotifier('warning', {
    style: {
      backgroundColor: '#F59E0B',
      color: '#FFF',
    },
    icon: <WarningIcon />,
  }),
  error: createNotifier('error', {
    style: {
      backgroundColor: '#f00',
      color: '#FFF',
    },
    icon: <XCircleIcon />,
  }),
}

export { notify }
